import { useCallback, useMemo, useRef, useState } from "react";
import { useCustom } from "@refinedev/core";
import {
  useExperienceAddons,
  type AudioDocResult,
  type ContentRead,
  type ExperienceAddons,
  type PodcastResult,
  type QuickRecallResult,
  type QuizResult,
  type StandoutsResult,
  type SummaryResult,
  type TabKey,
} from "@libertify/experience-viewer";
import type { AddOnStatus, AddOnType } from "pages/media/types";
import type { ProjectAddOnsResponse } from "pages/create-experience/data/addonApi";
import { useAddOnStatuses, type ReadyToast } from "./useAddOnStatuses";
import { useMediaStatus } from "./useMediaStatus";
import { useRefetchPublicationOnGeneration } from "./useRefetchPublicationOnGeneration";

export interface UseKnowledgeHubAddOnsArgs {
  projectId: string;
  /** Backend API base URL (`API_URL`). */
  apiBaseUrl: string;
  /** Session bearer for the authed add-on reads. */
  token?: string | null;
  /**
   * Whether the video's content already exists — the video is the project's
   * own MEDIA (not the publication's, and not an add-on asset), so the page
   * supplies its readiness + status. See MED-6969.
   */
  videoReady: boolean;
  /** The project's own media status — drives the video tab. */
  mediaStatus?: string;
  /**
   * Refetch the publication (its document path) as generation progresses —
   * the video no longer needs this (see `videoReady`/`mediaStatus` above).
   * Add-on asset content is re-pulled internally.
   */
  onRefreshPublication?: () => void;
}

export interface UseKnowledgeHubAddOnsResult {
  /** Add-on content (unchanged from `useExperienceAddons`). */
  quiz: QuizResult;
  summary: SummaryResult;
  podcast: PodcastResult;
  audioDoc: AudioDocResult;
  quickRecall: QuickRecallResult;
  standouts: StandoutsResult;
  addonsLoading: boolean;
  /** The project's own documents (exist from creation) — the always-available
   *  document source; the publication's documents override when present. */
  projectDocuments: ExperienceAddons["projectDocuments"];
  /** Org + chat settings from the project read — the publication-free source
   *  for the chat socket when the instance doesn't exist (videoless). */
  projectInfo: ExperienceAddons["projectInfo"];
  /** True while the tab's add-on is still generating (drives its loading UI). */
  isTabGenerating: (key: TabKey) => boolean;
  /** True when the tab's add-on generation failed (drives its error UI). */
  isTabErrored: (key: TabKey) => boolean;
  /** Add-ons that just became ready (drives the ready toasts). */
  readyToasts: ReadyToast[];
  dismissToast: (id: number) => void;
  /**
   * Tabs that genuinely transitioned to ready during this mount — the
   * eligibility gate for the unread green dot (see `useAddOnStatuses`).
   * A tab already ready on load is never in this set.
   */
  justReadyTabs: Set<TabKey>;
  /**
   * The add-ons this project has enabled — the source of truth for which
   * Knowledge Hub tabs exist (see `resolveVisibleTabs`).
   */
  enabledAddOns: AddOnType[];
  /**
   * True while the enabled-add-ons read is in flight. Separate from
   * `addonsLoading` (the project/asset read): an empty `enabledAddOns` means
   * "no add-ons" only once this is false, so a consumer that hides the panel
   * on an empty list must wait for it or it will hide on first render.
   */
  enabledAddOnsLoading: boolean;
  /** How many of the generated pool one quiz attempt serves
   *  (`QuizFunctionalSettings.nb_of_questions_per_subset`, MED-7065).
   *  Undefined until the add-ons read lands → `<Quiz>` serves the pool. */
  quizQuestionsPerAttempt: number | undefined;
  /** Whether the podcast starts playing as soon as its player opens
   *  (`PodcastFunctionalSettings.auto_play_on_open`, MED-7105). Undefined
   *  until the add-ons read lands → no auto-play. */
  podcastAutoPlay: boolean | undefined;
  /** Whether the podcast player shows its playback-rate buttons
   *  (`PodcastFunctionalSettings.playback_speed_control`). Undefined until
   *  the add-ons read lands → `<TrackPlayer>` defaults to shown. */
  podcastSpeedControl: boolean | undefined;
  /** Where the Knowledge Hub sidebar sits — the project's
   *  `general_functional_settings.side_panel_position` (default right). */
  sidebarPosition: "left" | "right";
  /**
   * Re-read every present add-on's content — the same re-pull an "add-on
   * ready" event triggers. For the out-of-date Update: a page opened before
   * an edit still holds the old content, and the backend doesn't say which
   * add-on changed, so all of them are re-read (small JSON each, in parallel).
   */
  refetchAddOns: () => void;
  /**
   * The Chatbot add-on's generation status: live WS report, else the project
   * manifest. Undefined = generation hasn't started yet (no state row).
   */
  chatbotStatus: AddOnStatus | undefined;
}

/**
 * Orchestrates the experience-v2 Knowledge Hub realtime state so
 * `ExperiencePage` consumes a single hook. Composes:
 *  - `useExperienceAddons` — add-on content (quiz/summary/podcast/audioDoc), re-pulled
 *    when an add-on finishes (`onAddOnReady` → bump the asset refetch).
 *  - `useAddOnStatuses` — per-add-on generation status + ready toasts.
 *  - `useMediaStatus` — the video tab's status + toast (video is media).
 *  - `useRefetchPublicationOnGeneration` — refetches the publication (document
 *    path) as the project generates.
 * It merges the add-on + media `statusByTab` and toast queues into one result.
 */
export function useKnowledgeHubAddOns({
  projectId,
  apiBaseUrl,
  token,
  videoReady,
  mediaStatus,
  onRefreshPublication,
}: UseKnowledgeHubAddOnsArgs): UseKnowledgeHubAddOnsResult {
  // Bumped when an add-on finishes so its newly-produced asset is re-pulled
  // (the add-on content lives in its asset, not the publication).
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  // Mirrors `refetchTrigger` so a WS handler can read the bumped value at once.
  const refetchTriggerRef = useRef(0);
  const bumpAssets = useCallback(() => {
    refetchTriggerRef.current += 1;
    setRefetchTrigger(refetchTriggerRef.current);
  }, []);
  // The re-read that brings a regenerated episode (see useExperienceAddons).
  const [podcastReadAwaited, setPodcastReadAwaited] = useState<number>();
  const handleAddOnReady = useCallback(
    (type: AddOnType) => {
      bumpAssets();
      if (type === "Podcast") setPodcastReadAwaited(refetchTriggerRef.current);
    },
    [bumpAssets]
  );

  const {
    loading: addonsLoading,
    presentAddOns,
    quiz,
    summary,
    podcast,
    audioDoc,
    quickRecall,
    standouts,
    projectDocuments,
    projectInfo,
    addOnStatuses,
  } = useExperienceAddons({
    apiBaseUrl,
    projectId,
    token,
    refetchTrigger,
    podcastReadAwaited,
  });

  // Which add-ons are enabled — the baseline for the realtime status, and the
  // source of truth for which Knowledge Hub tabs the page renders at all.
  // The same read carries every add-on's settings (same response the edit
  // page consumes); the functional ones drive how the viewer behaves.
  const { data: addOnsData, isLoading: enabledAddOnsLoading } =
    useCustom<ProjectAddOnsResponse>({
      url: `${apiBaseUrl}/media/projects/${projectId}/add-ons`,
      method: "get",
      queryOptions: { enabled: Boolean(projectId) },
    });
  const enabledAddOns = useMemo<AddOnType[]>(
    () => addOnsData?.data.enabled?.enabled_add_ons ?? [],
    [addOnsData?.data]
  );
  /** `QuizFunctionalSettings.nb_of_questions_per_subset` — how many of the
   *  generated pool one quiz attempt serves (MED-7065). */
  const quizQuestionsPerAttempt =
    addOnsData?.data.settings?.quiz_functional_settings
      ?.nb_of_questions_per_subset;
  /** `PodcastFunctionalSettings.auto_play_on_open` — start the episode as
   *  soon as its player mounts (MED-7105). */
  const podcastAutoPlay =
    addOnsData?.data.settings?.podcast_functional_settings?.auto_play_on_open;
  /** `PodcastFunctionalSettings.playback_speed_control` — show the
   *  playback-rate buttons. */
  const podcastSpeedControl =
    addOnsData?.data.settings?.podcast_functional_settings
      ?.playback_speed_control;
  // `GeneralFunctionalSettings.side_panel_position` — the API enum is
  // capitalized, the layout prop lowercase (default right).
  const sidebarPosition: "left" | "right" =
    addOnsData?.data.settings?.general_functional_settings
      ?.side_panel_position === "Left"
      ? "left"
      : "right";

  // Which add-ons already have content → counted as Generated (no loading
  // state, no re-toast for an already-finished experience). Video is media
  // (handled by useMediaStatus), not an add-on, so it's excluded here.
  // AudioDoc and Standouts count as ready on manifest presence rather than
  // parsed content: their content documents are still published empty
  // (MED-6934), and a generated-but-empty add-on must settle into the tab's
  // empty state, not shimmer as Generating forever.
  // Podcast reads its manifest STATUS, not presence: its primary asset is the
  // script, listed from the start of generation, so presence would mark it
  // ready before the episode exists — the real `Generated` event would then
  // look like a replay and never re-pull `secondary_asset_id` (MED-7042).
  const readyAddOns = useMemo<AddOnType[]>(() => {
    const ready: AddOnType[] = [];
    if (summary.content) ready.push("Summary");
    if (quiz.questions.length > 0) ready.push("Quiz");
    if (presentAddOns.includes("AudioDoc")) ready.push("AudioDoc");
    // OutOfDate: the episode still exists, it's only stale.
    if (
      addOnStatuses.Podcast === "Generated" ||
      addOnStatuses.Podcast === "OutOfDate"
    )
      ready.push("Podcast");
    if (quickRecall.cards.length > 0) ready.push("Flashcards");
    if (presentAddOns.includes("Standouts")) ready.push("Standouts");
    return ready;
  }, [
    summary.content,
    quiz.questions.length,
    presentAddOns,
    addOnStatuses.Podcast,
    quickRecall.cards.length,
  ]);

  // Add-ons whose generation ended in Error per the manifest — the baseline
  // that keeps a pre-mount failure from shimmering as Generating forever
  // (MED-7042 flagged this for Podcast, but it holds for every add-on).
  const erroredAddOns = useMemo<AddOnType[]>(
    () =>
      (Object.entries(addOnStatuses) as [AddOnType, AddOnStatus][])
        .filter(([, status]) => status === "Error")
        .map(([type]) => type),
    [addOnStatuses]
  );

  // Podcast's episode (project -> script -> mp3) is read AFTER the status
  // flips, so the tab stays loading until the read ends, and the dot/toast
  // show only if it brought a new episode.
  const contentReads = useMemo<Partial<Record<AddOnType, ContentRead>>>(
    () => ({ Podcast: podcast.read }),
    [podcast.read]
  );

  const {
    statusByTab: addOnStatusByTab,
    readyToasts: addOnToasts,
    dismissToast: dismissAddOnToast,
    justReadyTabs: addOnJustReadyTabs,
    liveStatusByType,
  } = useAddOnStatuses({
    projectId,
    enabledAddOns,
    readyAddOns,
    erroredAddOns,
    contentReads,
    onAddOnReady: handleAddOnReady,
  });
  const chatbotStatus = liveStatusByType.Chatbot ?? addOnStatuses.Chatbot;

  // Video = project media (see useMediaStatus): the video tab's status +
  // a single "video ready" toast, derived from the fetched media status.
  const {
    videoStatus,
    readyToasts: mediaToasts,
    dismissToast: dismissMediaToast,
    justReadyTabs: mediaJustReadyTabs,
  } = useMediaStatus({ projectId, mediaStatus, videoReady });

  // Keep the publication fresh (its document path) as the project generates —
  // the video is sourced from project media instead, see `videoReady` above.
  useRefetchPublicationOnGeneration(projectId, onRefreshPublication);

  // Merge add-on + media status/toasts so the page consumes one unified result.
  const statusByTab = useMemo<Partial<Record<TabKey, AddOnStatus>>>(
    () => ({
      ...addOnStatusByTab,
      ...(videoStatus ? { video: videoStatus } : {}),
    }),
    [addOnStatusByTab, videoStatus]
  );
  const readyToasts = useMemo<ReadyToast[]>(
    () => [...addOnToasts, ...mediaToasts],
    [addOnToasts, mediaToasts]
  );
  const justReadyTabs = useMemo<Set<TabKey>>(() => {
    const merged = new Set<TabKey>(addOnJustReadyTabs);
    mediaJustReadyTabs.forEach((tab) => merged.add(tab));
    return merged;
  }, [addOnJustReadyTabs, mediaJustReadyTabs]);
  const dismissToast = useCallback(
    (id: number) => {
      dismissAddOnToast(id);
      dismissMediaToast(id);
    },
    [dismissAddOnToast, dismissMediaToast]
  );

  const isTabGenerating = useCallback(
    (key: TabKey): boolean => {
      const status = statusByTab[key];
      return (
        status === "Generating" ||
        status === "Pending" ||
        status === "Initial" ||
        status === "WaitingForSummary"
      );
    },
    [statusByTab]
  );

  const isTabErrored = useCallback(
    (key: TabKey): boolean => statusByTab[key] === "Error",
    [statusByTab]
  );

  return {
    quiz,
    summary,
    podcast,
    audioDoc,
    quickRecall,
    standouts,
    addonsLoading,
    projectDocuments,
    projectInfo,
    isTabGenerating,
    isTabErrored,
    readyToasts,
    dismissToast,
    justReadyTabs,
    enabledAddOns,
    enabledAddOnsLoading,
    quizQuestionsPerAttempt,
    podcastAutoPlay,
    podcastSpeedControl,
    sidebarPosition,
    refetchAddOns: bumpAssets,
    chatbotStatus,
  };
}
